import * as React from 'react';
import type { DrawerProps } from '@fluentui/react-components';
import { makeStyles, OverlayDrawer } from '@fluentui/react-components';
import { useMediaQuery } from '@react-hook/media-query';
import { type ClientEntryPointEnum, useUserQuery } from '@foundershub/api-client';
import { setCopilotTextarea, useDispatch } from '../store';

interface DrawerContext {
  showDrawer: boolean;
  setShowDrawer: (show: boolean | ((shown: boolean) => boolean)) => void;
  children: React.ReactNode | null;
  setChildren: (Component: React.ReactNode | null) => void;
}

interface CopilotContext {
  openCopilot: (openedFrom: ClientEntryPointEnum) => void;
  closeCopilot: () => void;
  isSidecarOpen: boolean;
  // TODO: consider removing ability to manually set sidecar open
  // once we can control this using a field from the backend
  setSidecarOpen: (open: boolean) => void;
  isFREOpen: boolean;
  setFREOpen: (open: boolean) => void;
  isConfirmationOpen: boolean;
  setConfirmationOpen: (open: boolean) => void;
  openedFrom?: ClientEntryPointEnum;
}

export const DrawerContext = React.createContext<DrawerContext>({
  showDrawer: false,
  setShowDrawer: () => {},
  setChildren: () => {},
  children: null,
});

export const CopilotContext = React.createContext<CopilotContext>({
  openCopilot: () => {},
  closeCopilot: () => {},
  isSidecarOpen: false,
  setSidecarOpen: () => {},
  isFREOpen: false,
  setFREOpen: () => {},
  isConfirmationOpen: false,
  setConfirmationOpen: () => {},
});

export function DrawerProvider({ children }: { children: React.ReactNode }) {
  const { data: user, isError: isUserLoggedOut } = useUserQuery();
  const dispatch = useDispatch();
  const [showDrawer, setShowDrawer] = React.useState(false);
  const [drawerChildren, setChildren] = React.useState<React.ReactNode>(null);
  const [sidecarOpen, setSidecarOpen] = React.useState(false);
  const [freOpen, setFREOpen] = React.useState(false);
  const [confirmationOpen, setConfirmationOpen] = React.useState(false);
  const [openedFrom, setOpenedFrom] = React.useState<ClientEntryPointEnum>();

  function setCopilotOpenWithDrawer(open: boolean) {
    return isUserLoggedOut
      ? () => {}
      : open
        ? (openedFrom?: ClientEntryPointEnum) => {
            const openedFromForm = openedFrom == 'ata-form' || openedFrom == 'support-form';
            if (!user!.signed_copilot_agreement_on) {
              setFREOpen(openedFromForm || !sidecarOpen); // leave the sidecar open if it's already open
            } else if (openedFromForm) {
              setConfirmationOpen(true);
            } else {
              setSidecarOpenWithDrawer(true);
            }
            setOpenedFrom(openedFrom);
          }
        : () => {
            setFREOpen(false);
            setConfirmationOpen(false);
            setSidecarOpenWithDrawer(false);
          };
  }

  function setSidecarOpenWithDrawer(open: boolean) {
    if (!open) {
      dispatch(setCopilotTextarea(''));
    }
    setSidecarOpen(open);
    setShowDrawer(false);
  }

  return (
    <DrawerContext.Provider
      value={{
        showDrawer,
        setShowDrawer,
        setChildren,
        children: drawerChildren,
      }}
    >
      <CopilotContext.Provider
        value={{
          openCopilot: setCopilotOpenWithDrawer(true),
          closeCopilot: setCopilotOpenWithDrawer(false),
          isSidecarOpen: sidecarOpen,
          setSidecarOpen: setSidecarOpenWithDrawer,
          isFREOpen: freOpen,
          setFREOpen,
          isConfirmationOpen: confirmationOpen,
          setConfirmationOpen,
          openedFrom,
        }}
      >
        {children}
      </CopilotContext.Provider>
    </DrawerContext.Provider>
  );
}

export function FoundersHubUserOverlayDrawer(props: DrawerProps) {
  const classes = useStyles();
  const isSmallScreen = useMediaQuery('screen and (max-width: 430px)');
  const [topOffset, setTopOffset] = React.useState(0);

  const handleScroll = () => {
    if (document.getElementsByTagName('header').length === 0) {
      setTopOffset(0);
      return;
    }

    const headerHeight = document.getElementsByTagName('header')[0].clientHeight ?? 0;
    const headerTopY = document.getElementsByTagName('header')[0].getBoundingClientRect().top ?? 0;
    const offset = headerTopY > -1 * headerHeight ? headerTopY + headerHeight : 0;
    setTopOffset(offset);
  };

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const responsiveWidth = isSmallScreen ? '100%' : '420px';
  const { children, showDrawer } = React.useContext(DrawerContext);

  return (
    <OverlayDrawer
      position="end"
      open={showDrawer}
      style={{
        width: 'auto',
        minWidth: responsiveWidth,
        top: `${topOffset}px`,
        height: `calc(100vh - ${topOffset}px)`,
      }}
      className={classes.drawer}
      backdrop={{ className: classes.backdrop, style: { top: `${topOffset}px` } }}
      {...props}
    >
      {children}
    </OverlayDrawer>
  );
}

const useStyles = makeStyles({
  drawer: {
    zIndex: 1e14,
  },
  backdrop: {
    position: 'fixed',
    zIndex: 1e13,
  },
});
